/** What Repoworks is doing while a message is sent and a turn starts.
 *
 * The backend (``app/services/progress.py``) reports each wait as a
 * phase — ``{scope, phase, params, progress?, started_at}`` — with keys
 * and counts only; this module turns one into words in the reader's
 * language and decides when to show it:
 *
 * - a phase shows only after ``SHOW_AFTER_MS``, so a quick step never
 *   flickers past;
 * - the time spent joins it after ``ELAPSED_AFTER_MS``;
 * - a first clone explains itself after ``CLONE_HINT_AFTER_MS``.
 *
 * All three are measured from when THIS page first saw the phase
 * (``seenAt``), not from the server's ``started_at``: the browser's clock
 * may be off by more than 400 ms. ``started_at`` tells a new phase from
 * the same one counting on.
 */
import type { TFunction } from "i18next";

import { formatNumber } from "@/i18n/format";

export const SHOW_AFTER_MS = 400;
export const ELAPSED_AFTER_MS = 5_000;
export const CLONE_HINT_AFTER_MS = 60_000;

export const PROGRESS_PHASES = [
  "importing_project",
  "cloning_project",
  "fetching_changes",
  "updating_references",
  "queued",
  "preparing_editables",
  "updating_context",
  "preparing_agent",
  "starting_agent",
] as const;

export type ProgressPhaseKey = (typeof PROGRESS_PHASES)[number];
export type ProgressScope = "send" | "turn";

const KNOWN_PHASES: ReadonlySet<string> = new Set(PROGRESS_PHASES);

/** A phase as the server sends it, parsed. ``phase: null`` clears. */
export type ProgressWire = {
  scope: ProgressScope;
  phase: ProgressPhaseKey | null;
  params: { position?: number; provider?: string };
  progress: { done: number; total: number } | null;
  startedAt: string | null;
  turnId: string | null;
  /** The server reporter it came from; a clear naming one removes only
   *  that reporter's phase (./sessionProgress). */
  origin: string | null;
};

/** A phase being shown: what the server said, and when this page first
 *  saw it. */
export type ProgressPhase = ProgressWire & {
  phase: ProgressPhaseKey;
  seenAt: number;
};

function isCount(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}

/** The server's phase event (a ``session.progress`` payload or a turn
 *  stream's ``phase`` data), or ``null`` when it isn't one this SPA can
 *  word: better the words it had than a raw key. */
export function parseProgress(raw: unknown): ProgressWire | null {
  if (!raw || typeof raw !== "object") return null;
  const d = raw as Record<string, unknown>;
  const scope = d["scope"];
  if (scope !== "send" && scope !== "turn") return null;
  const key = d["phase"];
  if (key !== null && (typeof key !== "string" || !KNOWN_PHASES.has(key))) return null;
  const rawParams =
    d["params"] && typeof d["params"] === "object"
      ? (d["params"] as Record<string, unknown>)
      : {};
  const params: ProgressWire["params"] = {};
  if (isCount(rawParams["position"])) params.position = rawParams["position"];
  if (typeof rawParams["provider"] === "string" && rawParams["provider"]) {
    params.provider = rawParams["provider"];
  }
  const rawProgress = d["progress"] as Record<string, unknown> | undefined;
  const progress =
    rawProgress && isCount(rawProgress["done"]) && isCount(rawProgress["total"])
      ? { done: rawProgress["done"], total: rawProgress["total"] }
      : null;
  return {
    scope,
    phase: key as ProgressPhaseKey | null,
    params,
    progress,
    startedAt: typeof d["started_at"] === "string" ? d["started_at"] : null,
    turnId: typeof d["turn_id"] === "string" ? d["turn_id"] : null,
    origin: typeof d["origin"] === "string" ? d["origin"] : null,
  };
}

/** ``wire`` as the phase shown, keeping ``previous``'s ``seenAt`` when it
 *  is the same phase counting on (same scope, key, start and turn). */
export function stampProgress(
  wire: ProgressWire & { phase: ProgressPhaseKey },
  previous: ProgressPhase | null,
  now: number,
): ProgressPhase {
  const same =
    previous !== null &&
    previous.scope === wire.scope &&
    previous.phase === wire.phase &&
    previous.startedAt === wire.startedAt &&
    previous.turnId === wire.turnId;
  return {
    scope: wire.scope,
    phase: wire.phase,
    params: wire.params,
    progress: wire.progress,
    startedAt: wire.startedAt,
    turnId: wire.turnId,
    origin: wire.origin,
    seenAt: same ? previous.seenAt : now,
  };
}

/** The ``queued`` phase from what the send answered (``state: "queued"``,
 *  ``queue_position``) or the turn stream's ``queued`` event. */
export function queuedPhase(
  position: number | null | undefined,
  turnId: string | null,
  now: number,
): ProgressPhase {
  return {
    scope: "send",
    phase: "queued",
    params: isCount(position) ? { position } : {},
    progress: null,
    startedAt: null,
    turnId,
    origin: null,
    seenAt: now,
  };
}

/** What the turn is doing, for the activity pill: its stream's phase; or,
 *  until that stream has said anything at all, the queue the send
 *  answered with (``state: "queued"``) for this turn. Once the stream has
 *  spoken — a reconnect that replays from nothing included — the send's
 *  answer is stale and never comes back. */
export function turnPhaseFor({
  streamPhase,
  heard,
  queuedAtSend,
  activeTurnId,
}: {
  streamPhase: ProgressPhase | null;
  heard: boolean;
  queuedAtSend: ProgressPhase | null;
  activeTurnId: string | null;
}): ProgressPhase | null {
  if (streamPhase) return streamPhase;
  if (heard || !queuedAtSend || queuedAtSend.turnId !== activeTurnId) return null;
  return queuedAtSend;
}

export type ProgressTiming = {
  /** Past ``SHOW_AFTER_MS``: say the phase instead of the words before. */
  shown: boolean;
  /** The time spent, once past ``ELAPSED_AFTER_MS``. */
  elapsedMs: number | null;
  /** A first clone past ``CLONE_HINT_AFTER_MS``: explain the wait. */
  hint: boolean;
};

/** When to say ``phase`` and what to add to it. ``lineSince`` is when the
 *  line showing it first saw a phase: the 400 ms gate holds only until the
 *  line's first phase is shown — a step after it replaces the one before
 *  at once, never through the old words. The time spent and the clone
 *  hint count per phase. */
export function progressTiming(
  phase: ProgressPhase,
  now: number,
  lineSince: number = phase.seenAt,
): ProgressTiming {
  const age = now - phase.seenAt;
  return {
    shown: now - Math.min(lineSince, phase.seenAt) >= SHOW_AFTER_MS,
    elapsedMs: age >= ELAPSED_AFTER_MS ? age : null,
    hint: phase.phase === "cloning_project" && age >= CLONE_HINT_AFTER_MS,
  };
}

/** The phase in the reader's words. ``provider`` names the agent when the
 *  server didn't. */
export function progressLabel(
  t: TFunction,
  phase: ProgressPhase,
  opts: { provider?: string } = {},
): string {
  switch (phase.phase) {
    case "importing_project":
      return t("progress.importing_project");
    case "cloning_project":
      return t("progress.cloning_project");
    case "fetching_changes":
      return t("progress.fetching_changes");
    case "updating_references":
      return t("progress.updating_references");
    case "queued": {
      const position = phase.params.position;
      // ``position`` counts the turn itself: 1 is next in line.
      if (!position) return t("progress.queued");
      return t("progress.queued_ahead", { count: Math.max(0, position - 1) });
    }
    case "preparing_editables":
      return t("progress.preparing_editables");
    case "updating_context":
      return t("progress.updating_context");
    case "preparing_agent":
      return t("progress.preparing_agent");
    case "starting_agent": {
      const provider = phase.params.provider || opts.provider;
      return provider
        ? t("progress.starting_agent", { provider })
        : t("progress.starting_agent_generic");
    }
  }
}

/** ``done/total`` for a counted phase, in the UI language's digits. */
export function formatProgressCount(progress: { done: number; total: number }): string {
  return `${formatNumber(progress.done)}/${formatNumber(progress.total)}`;
}

/** Time spent as ``m:ss``: short, and the same in every language. */
export function formatElapsedClock(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}
